import React, { useState, useEffect } from "react";
import ClassNames from "classnames";
import styles from "./index.module.scss";
import { dayJS, utcToLocal } from "../../utils/datetime";
import { apiRequest } from "../../utils/api";
import AlertDialog from "../../components/AlertDialog";
import Button from "../../components/Button";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import _ from "lodash";

import CheckpointScoreSubject from "../../components/CheckpointScoreSubject";
import { useNotification } from "../../context/notification";
import { getLocalValue } from "../../utils/auth";
import { useTranslation } from "react-i18next";

function ValidationActivityDialog({
  dialogOpen,
  setDialogOpen,
  actionsList,
  activeCheckpoint,
  onChange,
}) {
  const [actionID, setActionID] = useState(null);

  useEffect(() => {
    if (dialogOpen) {
      setActionID(parseInt(activeCheckpoint.action_at_checkpoint));
    }
  }, [activeCheckpoint, dialogOpen]);

  return (
    <AlertDialog
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
      }}
      title={`Checkpoint validation activity`}
      body={
        <>
          {actionsList && actionsList.length > 0 && (
            <RadioGroup
              value={actionID}
              onChange={e => setActionID(parseInt(e.target.value))}
            >
              {actionsList.map((action, index) => {
                return (
                  <FormControlLabel
                    key={index}
                    value={action.id}
                    control={<Radio />}
                    label={action.action_checkpoint}
                  />
                );
              })}
            </RadioGroup>
          )}
        </>
      }
      footer={
        <>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            disabled={!actionID}
            color="primary"
            onClick={() => {
              onChange(activeCheckpoint, actionID);
            }}
          >
            Confirm
          </Button>
        </>
      }
    />
  );
}

export default function CheckpointScoreGrid({
  checkpoints,
  setCheckpoints,
  finalScores,
  setFinalScores,
  dateOverride,
  setIsFilledAndValid,
  disabled,
  ...props
}) {
  const { t } = useTranslation();
  const uid = getLocalValue("uid");
  const [headerDates, setHeaderDates] = useState([]);
  const [reviewDate, setReviewDate] = useState(
    new Date(utcToLocal(props.reviewDate))
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionsAtCheckpoints, setActionsAtCheckpoints] = useState(null);
  const [activeCheckpoint, setActiveCheckpoint] = useState({}); // cpObj
  const { addNotification } = useNotification();

  // sessionID
  useEffect(() => {
    // get actions at checkpoints
    apiRequest
      .get(
        `/api/getActionsAtCheckpoints?id=${props.sessionID}&action_checkpoint=string&uid=${uid}`
      )
      .then(response => {
        const data = response.data.data;
        setActionsAtCheckpoints(data);
      })
      .catch(error => {
        console.error(error);
        setActionsAtCheckpoints([]);
      });
  }, []);

  useEffect(() => {
    setReviewDate(new Date(utcToLocal(props.reviewDate)));
  }, [props.reviewDate]);

  useEffect(() => {
    // compute header dates
    let newDates = [];
    checkpoints.forEach(cp => {
      // if a checkpoint's date hasn't existed in the new array yet..
      if (_.findIndex(newDates, o => o.date === cp.checkpoint_date) < 0) {
        // ..push a new object with that date
        newDates.push({ date: cp.checkpoint_date });
      }
    });

    newDates.forEach(dateObj => {
      checkpoints
        .filter(cp => cp.checkpoint_date === dateObj.date)
        .forEach(cp => {
          let cpDate = new Date(cp.checkpoint_date);
          cpDate.setHours(0); // set date boundary to 00:00 hrs
          let alreadyFillable = new Date() > cpDate;

          // if checkpoint can already be filled up and has no value, make header true
          if (!!!cp.checkpoint_progress_value && alreadyFillable) {
            dateObj.active = true;
          }
        });
    });

    // sort date ascendingly
    newDates.sort((a, b) => {
      let aTime = new Date(a.date);
      let bTime = new Date(b.date);
      aTime = aTime.getTime();
      bTime = bTime.getTime();

      return aTime - bTime;
    });

    setHeaderDates(newDates);
  }, [checkpoints]);

  return (
    <div className={styles.checkpointScoreGrid}>
      <ValidationActivityDialog
        actionsList={actionsAtCheckpoints}
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        activeCheckpoint={activeCheckpoint}
        // on dialog submit
        onChange={(activeCheckpoint, actionID) => {
          let index = -1;
          // find for active checkpoint in finalScores
          index = finalScores.findIndex(o => {
            return (
              o.subject_id === activeCheckpoint.subject_id &&
              o.final_date === activeCheckpoint.final_date
            );
          });
          // found object in finalScores Array
          if (index >= 0) {
            // update finalScores
            let newFinalScores = _.cloneDeep(finalScores);
            newFinalScores[index]["action_at_checkpoint"] = actionID;
            setFinalScores(newFinalScores);
            setDialogOpen(false);
          } else {
            // find for active checkpoint in checkpoints
            index = checkpoints.findIndex(o => {
              return (
                o.subject_id === activeCheckpoint.subject_id &&
                o.checkpoint_date === activeCheckpoint.checkpoint_date
              );
            });
            // found object in checkpoints Array
            if (index >= 0) {
              // update checkpoints
              let newCheckpoints = _.cloneDeep(checkpoints);
              newCheckpoints[index]["action_at_checkpoint"] = actionID;
              setCheckpoints(newCheckpoints);
              setDialogOpen(false);
            } else {
              // has no matching object
              addNotification(
                t("error.canNotUpdateActionAtCheckPoint"),
                "error"
              );
            }
          }
        }}
      />
      <div className={styles.gridHeader}>
        {headerDates.map((dateObj, index) => {
          return (
            <div
              key={index}
              className={ClassNames(
                styles.gridHeaderItem,
                dateObj.active ? styles.active : ""
              )}
            >
              <div className={styles.label}>{`${t(
                "updateCheckpoint.checkpoint"
              )} ${index + 1}`}</div>
              <div className={styles.date}>
                {dayJS(dateObj.date).format("DD MMM YYYY")}
              </div>
            </div>
          );
        })}
        <div
          className={ClassNames(
            styles.gridHeaderItem,
            new Date() >= reviewDate ? styles.active : ""
          )}
        >
          <div className={styles.label}>{t("updateCheckpoint.final")}</div>
          <div className={styles.date}>
            {dayJS(reviewDate).format("DD MMM YYYY")}
          </div>
        </div>
      </div>
      <div className={styles.gridBody}>
        <CheckpointScoreSubject
          checkpoints={checkpoints}
          setCheckpoints={setCheckpoints}
          finalScores={finalScores}
          setFinalScores={setFinalScores}
          reviewDate={props.reviewDate}
          dateOverride={dateOverride}
          setDialogOpen={setDialogOpen}
          setActiveCheckpoint={setActiveCheckpoint}
          setIsFilledAndValid={setIsFilledAndValid}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
